import { useState, useEffect } from 'react';
import { PersonalInformationScreenPropertiesModel } from '@/services/platform/api/model';
import { useAuthStore } from '@/lib/core/stores';
import { Drawer, DrawerClose, DrawerContent, DrawerFooter } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Pin } from '@/components/ui/pin';
import { Icon } from '@/components/ui/icon';
import { useClientTranslation } from '@/lib/core';

export interface EditPinSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pageProperties: PersonalInformationScreenPropertiesModel | undefined;
  onSave: (pin: string) => Promise<void>;
}

export function EditPinSheet({ open, onOpenChange, pageProperties, onSave }: EditPinSheetProps) {
  const t = useClientTranslation();
  const setPin = useAuthStore(state => state.setPin);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!open) return;
    setNewPin('');
    setConfirmPin('');
    setError(undefined);
    setSuccess(false);
  }, [open]);

  function revalidate(np: string, cp: string) {
    if (np.length === 4 && cp.length === 4) {
      setError(np !== cp ? t('App.Labels.PinMismatch') : undefined);
    } else {
      setError(undefined);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(newPin);
      setPin(Number(newPin));
      setSuccess(true);
    } finally {
      setSaving(false);
    }
  }

  const canSave = newPin.length === 4 && confirmPin.length === 4 && !error;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        {/* Header */}
        <div className="flex items-center justify-between px-2 py-4">
          <div className="size-11" />
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px] uppercase">
            {pageProperties?.resetAccessPINModalTitle ?? t('App.Labels.ResetAccessPin')}
          </p>
          <div className="size-11" />
        </div>

        {success ? (
          /* Success confirmation */
          <div className="flex items-center gap-2 px-4 py-6">
            <Icon
              name="check-circle-solid"
              width={16}
              height={16}
              className="text-jncquoi-neutral-1000 shrink-0"
            />
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
              {pageProperties?.resetAccessPINSuccessMessage ?? t('App.Labels.AccessPinChanged')}
            </p>
          </div>
        ) : (
          /* Form */
          <div className="flex flex-col items-start gap-4 px-4 pt-6 pb-10">
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[24px] font-semibold">
              {pageProperties?.resetAccessPINTitle ?? t('App.Labels.CreateNewAccessPin')}
            </p>

            <div className="flex flex-col gap-1">
              <Label className="font-inter">
                {pageProperties?.newAccessPINLabel ?? t('App.Labels.NewAccessPin')}
              </Label>
              <Pin
                value={newPin}
                onChange={val => {
                  setNewPin(val);
                  revalidate(val, confirmPin);
                }}
                onComplete={() => {}}
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label className="font-inter">
                {pageProperties?.confirmNewAccessPINLabel ?? t('App.Labels.ConfirmNewAccessPin')}
              </Label>
              <Pin
                value={confirmPin}
                onChange={val => {
                  setConfirmPin(val);
                  revalidate(newPin, val);
                }}
                onComplete={() => {}}
                autoFocus={false}
                error={error}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <DrawerFooter>
          {success ? (
            <DrawerClose
              render={
                <Button variant="primary-solid" palette="dark" size="md">
                  {t('App.Labels.Done')}
                </Button>
              }
            />
          ) : (
            <>
              <Button
                variant="primary-solid"
                palette="dark"
                size="md"
                loading={saving}
                disabled={!canSave}
                onClick={handleSave}
              >
                {pageProperties?.resetAccessPINSaveChangesBtn ?? t('App.Labels.SaveChanges')}
              </Button>
              <DrawerClose
                render={
                  <Button variant="tertiary-link" palette="dark" size="sm">
                    {pageProperties?.resetAccessPINCancelBtn ?? t('App.Labels.Cancel')}
                  </Button>
                }
              />
            </>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
